import { useState, useMemo, useEffect } from "react";
import { PAYMENT_STATUSES, formatValue } from "../data/data";
import SearchBar from "../components/SearchBar";
import Pagination from "../components/Pagination";
import Modal from "../components/Modal";
import { getPayments, createPayment, updatePayment } from "../service/rpc";
import "../styles/PageShared.css";

const ITEMS_PER_PAGE = 6;
const EMPTY_FORM = { contract: "", amount: "", date: "", reference: "" };

export default function PaymentsPage() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [viewType, setViewType] = useState("list");

  //logic of kanban view in mobile 

  useEffect(()=>{
    if(window.innerWidth <= 992){
      setViewType("grid");
    }
  },[])

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        const offset = (currentPage - 1) * ITEMS_PER_PAGE;
        const domain = [['state', '=', 'paid']];
        if (search.trim()) {
          domain.push('|', ['name', 'ilike', search.trim()], ['partner_id', 'ilike', search.trim()]);
        }

        const { payments: data, count } = await getPayments(domain, ITEMS_PER_PAGE, offset);
        const mappedData = data.map(p => ({
          id: p.id,
          contract: p.partner_id ? p.partner_id[1] : "Unknown",
          amount: Math.abs(p.amount_company_currency_signed || 0),
          date: p.date && typeof p.date === 'string' ? p.date.split(' ')[0] : (p.date || "N/A"),
          reference: p.name || "N/A",
          status: 'Paid'
        }));
        setPayments(mappedData);
        setTotalItems(count);
      } catch (err) {
        console.error("Failed to fetch payments:", err);
        setError("Error loading payments. Please try again later.");
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [search, currentPage]);

  const openCreate = () => {
    setModalMode("create");
    setForm(EMPTY_FORM);
    setEditTarget(null);
    setModalOpen(true);
  };

  const openEdit = (e, p) => {
    e.stopPropagation();
    setModalMode("edit");
    setForm({ contract: p.contract, amount: String(p.amount), date: p.date, reference: p.reference });
    setEditTarget(p);
    setModalOpen(true);
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const payload = {
        date: form.date,
        amount: parseFloat(form.amount) || 0,
        ref: form.reference,
        payment_type: 'inbound',
        partner_type: 'customer',
      };

      if (modalMode === "create") {
        const newId = await createPayment(payload);
        setPayments(prev => [{
          id: newId,
          contract: form.contract || "Unknown",
          amount: parseFloat(form.amount) || 0,
          date: form.date,
          reference: form.reference,
          status: "Paid"
        }, ...prev]);
      } else {
        await updatePayment(editTarget.id, payload);
        setPayments(prev => prev.map(p => p.id === editTarget.id
          ? { ...p, amount: parseFloat(form.amount) || 0, date: form.date, reference: form.reference }
          : p
        ));
      }
      setModalOpen(false);
    } catch (err) {
      console.error("Save failed", err);
      alert("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const pagedData = payments;


  if (error) return (
    <main className="main">
      <div className="error-container" style={{ textAlign: 'center', padding: '100px 20px' }}>
        <div style={{ color: '#ef4444', fontSize: '48px', marginBottom: '20px' }}>⚠️</div>
        <h2 style={{ color: '#1e293b', marginBottom: '10px', fontFamily: 'Sora' }}>Something went wrong</h2>
        <p style={{ color: '#64748b', marginBottom: '20px' }}>{error}</p>
        <button className="btn-create" onClick={() => window.location.reload()}>Retry</button>
      </div>
    </main>
  );

  return (
    <main className="main">
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={modalMode === "create" ? "Record Payment" : "Edit Payment"}
        onSave={handleSave}
        saveLabel={modalMode === "create" ? "Record" : "Update"}
        saving={saving}
      >
        <div className="modal-form__group">
          <label className="modal-form__label">Contract / Partner</label>
          <input className="modal-form__input" name="contract" value={form.contract} onChange={handleFieldChange} placeholder="e.g. Acme Corp" />
        </div>
        <div className="modal-form__group">
          <label className="modal-form__label">Amount (₹)</label>
          <input className="modal-form__input" type="number" name="amount" value={form.amount} onChange={handleFieldChange} placeholder="0.00" />
        </div>
        <div className="modal-form__group">
          <label className="modal-form__label">Date</label>
          <input className="modal-form__input" type="date" name="date" value={form.date} onChange={handleFieldChange} />
        </div>
        <div className="modal-form__group">
          <label className="modal-form__label">Reference</label>
          <input className="modal-form__input" name="reference" value={form.reference} onChange={handleFieldChange} placeholder="e.g. INV/2024/001" />
        </div>
      </Modal>

      <div className="page-header">
        <h1 className="page-header__title">Payments</h1>
        {/* <div className="page-header__actions">
          <button className="btn-create" onClick={openCreate}>+ Record Payment</button>
        </div> */}
      </div>

      <div className="task-tab-header">
        <SearchBar search={search} setSearch={setSearch} placeholder="Search by contract or reference…" style={{ flex: 1, margin: 0 }} />
        <div className="view-toggle">
          <button className={`view-toggle__btn ${viewType === 'list' ? 'view-toggle__btn--active' : ''}`} onClick={() => setViewType('list')}>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" />
            </svg>
            List
          </button>
          <button className={`view-toggle__btn ${viewType === 'grid' ? 'view-toggle__btn--active' : ''}`} onClick={() => setViewType('grid')}>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" />
            </svg>
            Kanban
          </button>
        </div>
      </div>

      <div className={`table-card ${loading ? 'table-card--loading' : ''}`}>
        {loading && (
          <div className="list-loading-overlay">
            <div className="loader"></div>
            <p style={{ color: '#64748b', fontWeight: '500', fontFamily: 'Sora' }}>Loading...</p>
          </div>
        )}
        {viewType === "list" ? (
          <table className="table">
            <thead>
              <tr>
                <th className="table__th" style={{ width: '180px' }}>Date</th>
                <th className="table__th" style={{ width: '240px' }}>Contract</th>
                <th className="table__th">Reference</th>
                <th className="table__th" style={{ width: '180px' }}>Amount</th>
                <th className="table__th" style={{ width: '130px' }}>Status</th>
                <th className="table__th" style={{ textAlign: 'right' }}></th>
              </tr>
            </thead>
            <tbody>
              {pagedData.length === 0 ? (
                <tr><td className="table__empty" colSpan={6}>No paid payments found.</td></tr>
              ) : pagedData.map((p) => (
                <tr key={p.id} className="table__row">
                  <td className="table__td"><span className="table__client">{p.date}</span></td>
                  <td className="table__td"><span className="table__name">{p.contract}</span></td>
                  <td className="table__td"><span className="table__client">{p.reference}</span></td>
                  <td className="table__td"><span className="table__value" style={{ color: '#10b981' }}>{formatValue(p.amount)}</span></td>
                  <td className="table__td"><span className="badge badge--active">{p.status}</span></td>
                  <td className="table__td" style={{ textAlign: 'right' }}>
                    {/* <button className="btn-row-edit" onClick={(e) => openEdit(e, p)} title="Edit">
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                    </button> */}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="task-grid">
            {pagedData.length === 0 ? (
              <div className="table__empty" style={{ padding: '40px' }}>No payments match your filter.</div>
            ) : pagedData.map((p) => (
              <div key={p.id} className="kanban-card">
                <div className="kanban-card__top">
                  <span className={`badge badge--${p.status === "Paid" ? "active" : "pending"}`}>{p.status}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {/* <button className="btn-row-edit" onClick={(e) => openEdit(e, p)} title="Edit">
                      <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                    </button> */}
                  </div>
                </div>
                <h3 className="kanban-card__title" style={{ fontSize: '15px' }}>{p.contract}</h3>
                <div style={{ marginBottom: '16px', fontSize: '13px', color: '#64748b', fontWeight: '500' }}>Ref: {p.reference}</div>
                <div className="kanban-card__bottom">
                  <div style={{ fontWeight: '700', color: '#10b981' }}>{formatValue(p.amount)}</div>
                  <div className="kanban-card__due">{p.date}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        <Pagination currentPage={currentPage} totalItems={totalItems} itemsPerPage={ITEMS_PER_PAGE} onPageChange={setCurrentPage} />
      </div>
    </main>
  );
}
